import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProductSubscriptionStatusesEnum } from "dash-common";
import {
  toast,
  Modal,
  ModalContext,
  ViewerContext,
  Spinner,
  Icon,
  useBackendErrorMessage,
} from "dash-ui";
import {
  useCancelScheduledDowngradeMutation,
  GetCurrentAccountProductSubscriptionDocument,
} from "gql/generated.graphql";
import { getDisplayName } from "common/components/SubscriptionComponents/SubscriptionPlanCardV3/SubscriptionPlanCardV3.helpers";
import { formatEndDate } from "../SubscriptionChangeConfirmationModalV2/SubscriptionChangeConfirmationModalV2.helpers";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";

const MODAL_STYLES = {
  header: {
    background: "linear-gradient(to right, #f0fdf4, #d1fae5)",
    color: "#065f46",
  },
  button: "#059669",
};

interface CancelScheduledDowngradeModalProps {
  previousPlanName?: string;
}

export default function CancelScheduledDowngradeModal({
  previousPlanName,
}: CancelScheduledDowngradeModalProps) {
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialRevert",
  });
  const { t: tModal } = useTranslation(["pages", "common"]);
  const translate = useBackendErrorMessage();
  const { subscription, setSubscription } = useContext(ViewerContext);
  const { isOpenCurrent, toggleModal } = useContext(ModalContext);
  const isOpen = !!isOpenCurrent["CancelScheduledDowngradeModal"];

  const [cancelScheduledDowngrade, { loading }] =
    useCancelScheduledDowngradeMutation();

  const isTrialRevert =
    subscription?.status === ProductSubscriptionStatusesEnum.TRIALING;

  const closeModal = () => {
    toggleModal(false, "CancelScheduledDowngradeModal");
  };

  const handleConfirm = async () => {
    try {
      const result = await cancelScheduledDowngrade({
        refetchQueries: [GetCurrentAccountProductSubscriptionDocument],
        awaitRefetchQueries: true,
      });

      // On success, manually clean any zombie schedule in ViewerContext
      if (result.data && setSubscription && subscription) {
        // Force remove the schedule from the current subscription
        const cleanedSubscription = {
          ...subscription,
          schedule: null,
        };
        setSubscription(cleanedSubscription);
      }

      closeModal();
      toast.success(
        isTrialRevert
          ? t("keepSuccess")
          : tModal("pages:profilePage.billing.cancelDowngrade.success")
      );
    } catch (error: any) {
      toast.error(
        translate(
          error,
          tModal("pages:profilePage.billing.cancelDowngrade.error") || ""
        )
      );
    }
  };

  // Memoize modal data to prevent errors if schedule becomes null during render
  const modalData = useMemo(() => {
    if (!subscription?.schedule) return null;

    const downgradeDate =
      subscription.schedule.nextPhaseStart || subscription.currentPeriodEndAt;

    return {
      currentPlanName: getDisplayName(
        subscription.subscriptionProduct?.name || ""
      ),
      scheduledDowngradeTo: subscription.schedule.metadata?.target || "Basic",
      downgradeDate,
    };
  }, [
    subscription?.schedule,
    subscription?.subscriptionProduct?.name,
    subscription?.currentPeriodEndAt,
  ]);

  return (
    <Modal
      isOpen={isOpen && !!modalData && !!modalData.downgradeDate}
      onRequestClose={closeModal}
      className="max-w-md mx-auto"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 relative">
        {/* Header */}
        <div
          className="mb-6 -mx-6 -mt-6 px-6 pt-7 pb-7 rounded-t-lg relative"
          style={{ background: MODAL_STYLES.header.background }}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-1.5 bg-white hover:bg-gray-100 transition-colors rounded"
            aria-label={tModal("common:labels.close") || ""}
            disabled={loading}
          >
            <Icon name="close" size={16} color="#6B7280" />
          </button>
          <h2
            className="text-xl font-bold pr-8"
            style={{ color: MODAL_STYLES.header.color }}
          >
            {isTrialRevert
              ? t("keepTitle", { plan: modalData?.currentPlanName }) || ""
              : tModal("pages:profilePage.billing.cancelDowngrade.title")}
          </h2>
          <p
            className="text-sm mt-1"
            style={{ color: MODAL_STYLES.header.color }}
          >
            {isTrialRevert
              ? t("keepSubtitle")
              : tModal("pages:profilePage.billing.cancelDowngrade.subtitle")}
          </p>
        </div>

        {/* Content */}
        {modalData && (
          <div className="space-y-4 mb-6">
            {/* Current Plan Info */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="check" size={16} color={MODAL_STYLES.button} />
                <p className="font-semibold text-gray-900">
                  {tModal(
                    "pages:profilePage.billing.cancelDowngrade.keepPlan",
                    {
                      plan: modalData.currentPlanName,
                    }
                  )}
                </p>
              </div>
              <p className="text-sm text-gray-600 ml-6">
                {isTrialRevert
                  ? t("keepText", {
                      plan: modalData.currentPlanName,
                      date: formatDateString(modalData.downgradeDate),
                    })
                  : tModal(
                      "pages:profilePage.billing.cancelDowngrade.keepBenefits"
                    )}
              </p>
            </div>

            {/* What's Being Canceled */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="close" size={16} color="#d97706" />
                <p className="font-semibold text-amber-900">
                  {isTrialRevert
                    ? t("returnCanceledTitle", {
                        previousPlan: previousPlanName,
                      })
                    : tModal("pages:profilePage.billing.cancelDowngrade.title")}
                </p>
              </div>
              <p className="text-sm text-amber-700 ml-6">
                {isTrialRevert
                  ? t("returnCanceledText", {
                      previousPlan: previousPlanName,
                      date: formatDateString(modalData.downgradeDate),
                    })
                  : tModal(
                      "pages:profilePage.billing.cancelDowngrade.willBeCanceled",
                      {
                        plan: modalData.scheduledDowngradeTo,
                        date: formatEndDate(modalData.downgradeDate),
                      }
                    )}
              </p>
            </div>

            {/* Important Note */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Icon
                name="infoCircle"
                size={16}
                color="#2563eb"
                className="mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-blue-900">
                {isTrialRevert
                  ? t("cancelAgainNote", {
                      date: formatDateString(modalData.downgradeDate),
                    })
                  : tModal(
                      "pages:profilePage.billing.cancelDowngrade.rateNote"
                    )}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={closeModal}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            {tModal("common:labels.cancel")}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2.5 rounded-md font-medium text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            style={{ backgroundColor: MODAL_STYLES.button }}
            disabled={loading}
          >
            {loading && <Spinner size={16} color="white" />}
            {loading
              ? isTrialRevert
                ? t("keepLoading")
                : tModal("pages:profilePage.billing.cancelDowngrade.canceling")
              : isTrialRevert
              ? t("keepButton", { plan: modalData?.currentPlanName })
              : tModal(
                  "pages:profilePage.billing.cancelDowngrade.confirmButton"
                )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
